import { useMemo, useRef, useEffect } from "react";
import { useAuthToken } from "../utils/auth";
import * as api from "../services/api";

/**
 * Hook that provides API functions with authentication automatically included
 * This eliminates the need to manually pass tokens to each API call
 */
export const useApi = () => {
  const { getToken, isAuthenticated, isLoading } = useAuthToken();

  // Use refs to store the latest values without causing re-renders
  const getTokenRef = useRef(getToken);
  const isAuthenticatedRef = useRef(isAuthenticated);
  const isLoadingRef = useRef(isLoading);

  // Update refs when values change
  useEffect(() => {
    getTokenRef.current = getToken;
    isAuthenticatedRef.current = isAuthenticated;
    isLoadingRef.current = isLoading;
  });

  // Helper to wrap API calls with token - stable function using refs
  const withAuthRef = useRef(async <T>(
    apiCall: (token: string | null) => Promise<T>
  ): Promise<T> => {

    if (!isAuthenticatedRef.current) {
      throw new Error("User is not authenticated");
    }

    if (isLoadingRef.current) {
      // Wait a bit for auth to finish
      await new Promise((resolve) => setTimeout(resolve, 100));
    }

    const token = await getTokenRef.current();
    if (token) {
    } else {
      throw new Error("Failed to retrieve authentication token");
    }

    return apiCall(token);
  });

  const withAuth = withAuthRef.current;

  // Memorize the API object to prevent infinite loops in useEffect
  // Only depends on isAuthenticated and isLoading since withAuth is stable via ref
  return useMemo(
    () => ({
      isAuthenticated,
      isLoading,
      // Orders
      getOrders: (limit?: number, offset?: number) =>
        withAuth((token) => api.getOrders(limit, offset, token)),
      getOrdersByDate: (date: string, status?: string) =>
        withAuth((token) => api.getOrdersByDate(date, status, token)),
      getOrderById: (id: number) =>
        withAuth((token) => api.getOrderById(id, token)),
      getOrdersByCustomerPhone: (
        phone: string,
        limit?: number,
        offset?: number
      ) =>
        withAuth((token) =>
          api.getOrdersByCustomerPhone(phone, limit, offset, token)
        ),
      updateOrderStatus: (id: number, status: string, extra?: { documentType?: string; documentDate?: string; paymentType?: string; paymentDate?: string; tipoDocumento?: 'VENTA' | 'PEDIDO' }) =>
        withAuth((token) => api.updateOrderStatus(id, status, token, extra)),

      // Unified threads (WhatsApp + Email)
      getThreads: () =>
        withAuth((token) => api.getThreads(token)),
      getEmailIntakeDetail: (emailIntakeId: number) =>
        withAuth((token) => api.getEmailIntakeDetail(emailIntakeId, token)),

      // Conversations
      getAllConversations: () =>
        withAuth((token) => api.getAllConversations(token)),
      getConversationByPhone: (phone: string) =>
        withAuth((token) => api.getConversationByPhone(phone, token)),
      getConversationMessages: (conversationId: number, limit?: number) =>
        withAuth((token) =>
          api.getConversationMessages(conversationId, limit, token)
        ),
      sendManualMessage: (conversationId: number, message: string) =>
        withAuth((token) =>
          api.sendManualMessage(conversationId, message, token)
        ),
      setManualMode: (conversationId: number, enabled: boolean) =>
        withAuth((token) => api.setManualMode(conversationId, enabled, token)),

      // Products
      getProductById: (id: number) =>
        withAuth((token) => api.getProductById(id, token)),
      getProducts: (search?: string, limit?: number, page?: number) =>
        withAuth((token) => api.getProducts(search, limit, page, token)),
      updateProduct: (id: number, updates: { name?: string; price?: number; category?: string | null }) =>
        withAuth((token) => api.updateProduct(id, updates, token)),
      deleteProduct: (id: number) =>
        withAuth((token) => api.deleteProduct(id, token)),
      updateOrderItem: (
        orderId: number,
        itemId: number,
        updates: { productId?: number; quantity?: number; quantityUnit?: string }
      ) =>
        withAuth((token) =>
          api.updateOrderItem(orderId, itemId, updates, token)
        ),
      deleteOrderItem: (orderId: number, itemId: number) =>
        withAuth((token) => api.deleteOrderItem(orderId, itemId, token)),
      addOrderItem: (
        orderId: number,
        item: { productId: number; quantity: number; quantityUnit?: string }
      ) =>
        withAuth((token) => api.addOrderItem(orderId, item, token)),
      createOrder: (orderData: {
        customerName: string;
        customerPhone: string;
        deliveryAddress: string;
        deliveryComuna: string;
        items: Array<{
          productId: number;
          quantity: number;
        }>;
        customerEmail?: string;
        deliveryRecipientName?: string;
        paymentPreference?: 'efectivo' | 'tarjeta' | 'transferencia' | 'sin_preferencia';
        specialInstructions?: string;
        pdfFile?: File;
      }) =>
        withAuth((token) => api.createOrder(orderData, token)),
      
      // PDF Processing
      processPdfOrder: (pdfFile: File) =>
        withAuth((token) => api.processPdfOrder(pdfFile, token)),
      processDocumentOrder: (file: File) =>
        withAuth((token) => api.processDocumentOrder(file, token)),
      
      // Kameone Fichas (Clientes)
      getKameoneFichas: (rut?: string, razonSocial?: string) =>
        withAuth((token) => api.getKameoneFichas(rut, razonSocial, token)),
      getAllKameoneFichas: () =>
        withAuth((token) => api.getAllKameoneFichas(token)),
      updateOrderCustomer: (orderId: number, customerData: {
        customerRut: string;
        customerName?: string;
        customerPhone?: string;
        customerEmail?: string;
        customerGiro?: string;
        deliveryAddress?: string;
        deliveryComuna?: string;
        deliveryCity?: string;
      }) =>
        withAuth((token) => api.updateOrderCustomer(orderId, customerData, token)),
      updatePendingOrder: (orderId: number, orderData: {
        items: Array<{ productId: number; quantity: number; unitPrice?: number; pdfLineText?: string }>;
        customerRut?: string;
        customerName?: string;
        customerPhone?: string;
        customerEmail?: string;
        customerGiro?: string;
        deliveryAddress?: string;
        deliveryComuna?: string;
        deliveryCity?: string;
      }) =>
        withAuth((token) => api.updatePendingOrder(orderId, orderData, token)),

      // API Keys
      getApiKeys: () =>
        withAuth((token) => api.getApiKeys(token)),
      getApiKey: (keyName: string) =>
        withAuth((token) => api.getApiKey(keyName, token)),
      getApiKeyForEdit: (keyName: string) =>
        withAuth((token) => api.getApiKeyForEdit(keyName, token)),
      setApiKey: (keyName: string, value: string) =>
        withAuth((token) => api.setApiKey(keyName, value, token)),
      deleteApiKey: (keyName: string) =>
        withAuth((token) => api.deleteApiKey(keyName, token)),

      createProduct: (product: { name: string; sku: string; price: number; unidadDeNegocio?: string }) =>
        withAuth((token) => api.createProduct(product, token)),

      // Product Sync
      syncProducts: (confirmationToken: string) =>
        withAuth((token) => api.syncProducts(confirmationToken, token)),

      // Vendedores
      getVendedores: () =>
        withAuth((token) => api.getVendedores(token)),
      createVendedor: (name: string) =>
        withAuth((token) => api.createVendedor(name, token)),
      updateVendedor: (id: number, name: string) =>
        withAuth((token) => api.updateVendedor(id, name, token)),
      deleteVendedor: (id: number) =>
        withAuth((token) => api.deleteVendedor(id, token)),
      syncVendedoresFromKame: () =>
        withAuth((token) => api.syncVendedoresFromKame(token)),

      // Extraction audits (vista /check)
      getExtractionAudits: (date: string) =>
        withAuth((token) => api.getExtractionAudits(date, token)),

      // Client Addresses
      getClientAddresses: (rut?: string) =>
        withAuth((token) => api.getClientAddresses(token, rut)),
      createClientAddress: (input: api.CreateClientAddressInput) =>
        withAuth((token) => api.createClientAddress(input, token)),
      updateClientAddress: (id: number, updates: api.UpdateClientAddressInput) =>
        withAuth((token) => api.updateClientAddress(id, updates, token)),
      deleteClientAddress: (id: number) =>
        withAuth((token) => api.deleteClientAddress(id, token)),
      importClientAddressesFromKame: () =>
        withAuth((token) => api.importClientAddressesFromKame(token)),

      // Price Lists
      getPriceLists: () =>
        withAuth((token) => api.getPriceLists(token)),
      createPriceList: (name: string) =>
        withAuth((token) => api.createPriceList(name, token)),
      getPriceListById: (id: number) =>
        withAuth((token) => api.getPriceListById(id, token)),
      updatePriceList: (id: number, name: string) =>
        withAuth((token) => api.updatePriceList(id, name, token)),
      deletePriceList: (id: number) =>
        withAuth((token) => api.deletePriceList(id, token)),
      getPriceListItems: (listId: number, limit?: number, offset?: number, search?: string) =>
        withAuth((token) => api.getPriceListItems(listId, limit, offset, search, token)),
      addPriceListItems: (listId: number, items: { productId: number; price: number }[]) =>
        withAuth((token) => api.addPriceListItems(listId, items, token)),
      updatePriceListItem: (listId: number, itemId: number, price: number) =>
        withAuth((token) => api.updatePriceListItem(listId, itemId, price, token)),
      deletePriceListItem: (listId: number, itemId: number) =>
        withAuth((token) => api.deletePriceListItem(listId, itemId, token)),
      getPriceListClients: (listId: number) =>
        withAuth((token) => api.getPriceListClients(listId, token)),
      deletePriceListClient: (listId: number, clientId: number) =>
        withAuth((token) => api.deletePriceListClient(listId, clientId, token)),
      exportPriceListCsv: (listId: number) =>
        withAuth((token) => api.exportPriceListCsv(listId, token)),
      setDefaultPriceList: (priceListId: number | null) =>
        withAuth((token) => api.setDefaultPriceList(priceListId, token)),
      resolvePricesForPriceList: (listName: string, productIds: number[]) =>
        withAuth((token) => api.resolvePricesForPriceList(listName, productIds, token)),

      importPriceListCsv: (file: File) =>
        withAuth((token) => api.importPriceListCsv(file, token)),

      assignClientToPriceList: (razonSocial: string, listName: string | null) =>
        withAuth((token) => api.assignClientToPriceList(razonSocial, listName, token)),
      getCustomerPriceListAssignment: (razonSocial: string) =>
        withAuth((token) => api.getCustomerPriceListAssignment(razonSocial, token)),

      // Folio
      getNextFolio: (documentType?: string, mode: 'last_created' | 'last_kame' = 'last_kame') =>
        withAuth((token) => api.getNextFolio(documentType, mode, token)),
    }),
    [isAuthenticated, isLoading]
  );
};
